// Thin wrapper around the browser's Notifications API.
//
// Why a wrapper at all: makes the SSR / non-browser case a no-op (the
// type `Notification` is undefined in node), centralizes the "did the
// user enable this?" check so the component watcher can be terse, and
// gives us a single place to swap in a custom UI if we ever want
// in-app toast-style notifications as a fallback.
//
// Persistence: we don't store the on/off state ourselves — the
// browser's permission string IS the source of truth (granted /
// default / denied). If a user wants to silence Ember without
// affecting other sites, they revoke permission for embertime.* in
// the browser's site settings.

export type NotifPermission = 'granted' | 'denied' | 'default' | 'unsupported'

export function isSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window
}

/** True on iPhone/iPad — also recognizes iPad Safari which reports
 * itself as Mac with touch support since iPadOS 13. */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  if (/iPhone|iPad|iPod/.test(ua)) return true
  // iPad Safari masquerades as macOS — distinguish by touch + Apple platform
  const macWithTouch =
    /Macintosh/.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document
  return macWithTouch
}

/** True when the page runs as a saved-to-Home-Screen Web App rather
 * than a regular browser tab. Apple gates the Notifications API on
 * this — Safari tabs on iOS/iPadOS cannot deliver notifications even
 * if the API objects exist. */
export function isPWAStandalone(): boolean {
  if (typeof window === 'undefined') return false
  // Modern: matchMedia. Legacy iOS: navigator.standalone.
  const byMedia = window.matchMedia?.('(display-mode: standalone)').matches ?? false
  const byLegacy =
    'standalone' in window.navigator &&
    (window.navigator as { standalone?: boolean }).standalone === true
  return byMedia || byLegacy
}

/** Convenience: iOS Safari tab cannot deliver notifications. Even if
 * `Notification` exists (iOS 16.4+ exposes it in tabs too), the
 * permission prompt either no-ops or the notifications never fire. */
export function isIOSBrowserTab(): boolean {
  return isIOS() && !isPWAStandalone()
}

export function getPermission(): NotifPermission {
  if (!isSupported()) return 'unsupported'
  return Notification.permission
}

export async function requestPermission(): Promise<NotifPermission> {
  if (!isSupported()) return 'unsupported'
  // Safari < 16 used the callback signature; modern browsers return a
  // Promise. The Promise form is now widely supported so we use it
  // directly — Safari 16+ (2022) is our floor.
  const result = await Notification.requestPermission()
  return result
}

export interface ShowNotificationOpts {
  title: string
  body?: string
  /** Used to coalesce notifications — same tag replaces the previous
   * one rather than stacking. We use the chat id for chat notifications
   * so multiple messages from the same chat don't pile up. */
  tag?: string
  /** Path inside the SPA — clicking the notification focuses the tab
   * and navigates here. */
  navigateTo?: string
  /** When true, the notification stays until the user interacts (vs.
   * auto-dismiss after a few seconds). Off by default. */
  requireInteraction?: boolean
}

export function showNotification(opts: ShowNotificationOpts): Notification | null {
  if (!isSupported() || Notification.permission !== 'granted') return null
  try {
    const n = new Notification(opts.title, {
      body: opts.body,
      tag: opts.tag,
      requireInteraction: opts.requireInteraction ?? false,
      // Same-origin URL — use the 192px PNG for best cross-platform
      // rendering in the OS notification center. SVG would also work
      // in modern Chrome/Firefox but some Linux DEs (kde, dunst)
      // rasterize poorly from SVG.
      icon: `${import.meta.env.BASE_URL}icon-192.png`,
      silent: false,
    })
    if (opts.navigateTo) {
      n.onclick = () => {
        window.focus()
        // Use a custom event so the router-aware mount point can pick
        // it up and call its `useNavigate` — keeps this module router-
        // agnostic.
        window.dispatchEvent(
          new CustomEvent('embertime:notification-click', { detail: { to: opts.navigateTo } }),
        )
        n.close()
      }
    }
    return n
  } catch {
    return null
  }
}
